import React from "react";
import {
  ENGINE_LABELS,
  agentAlsoProposes,
  carriesAsStructure,
  groupHeuristicsBySource,
  heuristicCategoryLabel,
  heuristicTaskIds,
  isHeuristicTickable,
  isUncreditedAdoption,
  readHeuristicStatus,
  tickDisabledReason,
  tickEffectNote,
} from "../../utils/redesignProvenance";

// The save checklist: every heuristic that went into the version being written, and
// — for the two a version can physically carry — a switch to say whether it should
// be written at all.
//
// Two rules shape all of this and are worth stating once here rather than repeating
// at each site below:
//
//  1. The engines' own words are reproduced, never rewritten. `measured_verdict`,
//     `rationale`, `basis`, `not_saved_reason` and `not_persistable_reason` are
//     printed as they arrive. A row is an engine's account of its own work; a
//     paraphrase written on this side would be our opinion in the engine's voice.
//
//  2. A row that did NOT make it into the structure is still a row. Hiding
//     rejected_by_measurement or not_representable would leave the analyst with a
//     clean list and no idea that three of their heuristics evaporated — which is
//     the precise failure this whole surface exists to prevent.

const TONE = {
  ok: { ink: "#166534", bg: "#dcfce7", line: "#86efac", rail: "#16a34a" },
  neutral: { ink: "#3730a3", bg: "#eef2ff", line: "#c7d2fe", rail: "#6366f1" },
  muted: { ink: "#6b7280", bg: "#f3f4f6", line: "#e5e7eb", rail: "#d1d5db" },
  warn: { ink: "#92400e", bg: "#fffbeb", line: "#fde68a", rail: "#f59e0b" },
  error: { ink: "#991b1b", bg: "#fef2f2", line: "#fca5a5", rail: "#ef4444" },
};

const SOURCE_INK = { agent: "#0e6b68", rl: "#5b21b6", bpr: "#1d4ed8" };
const SOURCE_BG = { agent: "#ccfbf1", rl: "#ede9fe", bpr: "#dbeafe" };

const labelStyle = {
  fontSize: 12,
  fontWeight: 700,
  color: "#6b7280",
  marginBottom: 8,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
};

function Chip({ ink, bg, line, children, title, mono }) {
  return (
    <span
      title={title}
      style={{
        fontSize: 10,
        fontWeight: 700,
        color: ink,
        background: bg,
        border: `1px solid ${line}`,
        borderRadius: 999,
        padding: "1px 7px",
        whiteSpace: "nowrap",
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined,
      }}
    >
      {children}
    </span>
  );
}

function Banner({ tone, title, children }) {
  const t = TONE[tone] || TONE.warn;
  return (
    <div
      style={{
        fontSize: 12.5,
        color: t.ink,
        background: t.bg,
        border: `1px solid ${t.line}`,
        borderLeft: `4px solid ${t.rail}`,
        borderRadius: 8,
        padding: "8px 12px",
        marginBottom: 8,
      }}
    >
      <div style={{ fontWeight: 700 }}>⚠ {title}</div>
      {children && <div style={{ marginTop: 2 }}>{children}</div>}
    </div>
  );
}

/**
 * The gateway a ticked parallelism row actually produced, with the what-if's own
 * before/after.
 *
 * `measured_improvement === false` on an analyst-adopted row is called out rather
 * than dropped: the tick applied a gateway the measurement could not credit with a
 * cycle-time gain. Both facts are true and neither cancels the other, so the row
 * carries both — the analyst's decision stands, and its cost is legible.
 */
function SavedGateway({ row, fmtMin }) {
  const g = row?.saved_gateway;
  if (!g) return null;
  const min = (v) => (v == null ? "—" : fmtMin ? fmtMin(v) : `${v} min`);
  const branches = Array.isArray(g.branch_task_ids) ? g.branch_task_ids : [];
  return (
    <div style={{ marginTop: 5, fontSize: 11.5, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 6, padding: "5px 9px" }}>
      <span style={{ fontWeight: 700 }}>Gateway written:</span> PARALLEL after task{" "}
      <b>{g.after_task_id ?? "—"}</b>
      {branches.length > 0 && <> splitting {branches.map((b) => `#${b}`).join(" ∥ ")}</>}
      {(g.as_is_cycle_time_minutes != null || g.to_be_cycle_time_minutes != null) && (
        <span style={{ marginLeft: 8, fontVariantNumeric: "tabular-nums" }}>
          {min(g.as_is_cycle_time_minutes)} → <b>{min(g.to_be_cycle_time_minutes)}</b>
        </span>
      )}
      {isUncreditedAdoption(row) && (
        <div style={{ marginTop: 3, color: "#92400e", fontWeight: 600 }}>
          You adopted this pair and it is being saved, but the what-if could not measure any
          cycle-time gain from it. The gateway is real; the improvement is unproven.
        </div>
      )}
    </div>
  );
}

function HeuristicRow({ prov, row, checked, onToggle, disabled, fmtMin }) {
  const status = readHeuristicStatus(row);
  const tone = TONE[status.tone] || TONE.muted;
  const tickable = isHeuristicTickable(row);
  const lockReason = tickDisabledReason(row);
  // A tickable row the workflow cannot carry: the tick is honoured by recording it
  // against the saved redesign scenario instead. One checkbox, two outcomes — which
  // is why the row says which outcome it has.
  const recordsDecision = tickable && !carriesAsStructure(row);
  const taskIds = heuristicTaskIds(row);
  const wroteDifferentTasks =
    row.structural_task_ids?.length > 0 &&
    row.task_ids?.length > 0 &&
    row.structural_task_ids.join(",") !== row.task_ids.join(",");
  const agentToo = tickable && agentAlsoProposes(prov, row);
  const notSaved = row.status !== "saved_as_structure" ? row.not_saved_reason : "";

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        background: status.tone === "muted" ? "#fafafa" : "#fff",
        border: "1px solid #e5e7eb",
        borderLeft: `4px solid ${tone.rail}`,
        borderRadius: 8,
        padding: "8px 12px",
        opacity: status.tone === "muted" ? 0.75 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled || !tickable}
        onChange={() => onToggle(row)}
        title={lockReason || `Include ${row.name} in the saved structure`}
        aria-label={row.name}
        style={{ marginTop: 3, cursor: tickable && !disabled ? "pointer" : "not-allowed" }}
      />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 7, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>{row.name || row.code}</span>
          <Chip ink={SOURCE_INK[row.source] || "#374151"} bg={SOURCE_BG[row.source] || "#f3f4f6"} line="#00000014">
            {ENGINE_LABELS[row.source] || row.source}
          </Chip>
          {/* Never hidden when null — an unreachable catalog is a gap in our data,
              not a reason to drop a heuristic the engine really applied. */}
          <Chip ink="#6b7280" bg="#f9fafb" line="#e5e7eb" title="FBPM category">
            {heuristicCategoryLabel(row)}
          </Chip>
          <Chip ink={tone.ink} bg={tone.bg} line={tone.line} title={status.blurb}>
            {status.label}
          </Chip>
          {taskIds.length > 0 && (
            <span
              style={{ fontSize: 11, color: "#6b7280" }}
              title={
                wroteDifferentTasks
                  ? `Named by the engine: ${row.task_ids.join(", ")} — written into the structure in this order.`
                  : undefined
              }
            >
              {taskIds.map((t) => `#${t}`).join(" · ")}
              {wroteDifferentTasks && <span style={{ color: "#b45309" }}> (reordered)</span>}
            </span>
          )}
          {(row.dq_dimensions || row.affects || []).length > 0 && (
            <span style={{ display: "inline-flex", gap: 3 }}>
              {(row.dq_dimensions || row.affects).map((d) => (
                <Chip key={d} ink="#475569" bg="#eef2f7" line="#e2e8f0">
                  {String(d)}
                </Chip>
              ))}
            </span>
          )}
        </div>

        {row.basis && <div style={{ fontSize: 11.5, color: "#b45309", marginTop: 3 }}>because {row.basis}</div>}
        {row.measured_verdict && (
          <div style={{ fontSize: 12, color: "#374151", marginTop: 3 }}>{row.measured_verdict}</div>
        )}
        {row.rationale && row.rationale !== row.measured_verdict && (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 3 }}>{row.rationale}</div>
        )}

        {notSaved && (
          <div style={{ fontSize: 11.5, color: TONE.warn.ink, background: TONE.warn.bg, border: `1px solid ${TONE.warn.line}`, borderRadius: 6, padding: "4px 8px", marginTop: 4 }}>
            <b>Not written:</b> {notSaved}
          </div>
        )}
        {row.not_persistable_reason && (
          <div style={{ fontSize: 11.5, color: "#4b5563", marginTop: 4, fontStyle: "italic" }}>
            {row.not_persistable_reason}
          </div>
        )}

        {row.impact_computable === false && row.impact_note && (
          <div style={{ fontSize: 11.5, color: "#475569", marginTop: 4, display: "flex", alignItems: "baseline", gap: 6 }}>
            <Chip ink="#475569" bg="#f1f5f9" line="#e2e8f0">
              no immediate number change
            </Chip>
            <span>{row.impact_note}</span>
          </div>
        )}

        {/* The locked checkbox above says nothing about WHY it is locked, and a
            tooltip is not an answer to a control that looks broken. For these rows
            the reason and the action the analyst actually wants sit together. */}
        {recordsDecision && (
          <div style={{ marginTop: 5, fontSize: 11.5, color: checked ? "#4338ca" : "#6b7280", background: checked ? "#eef2ff" : "#f9fafb", border: `1px solid ${checked ? "#c7d2fe" : "#e5e7eb"}`, borderRadius: 6, padding: "5px 9px" }}>
            <b>{checked ? "Ticked — recorded with this version." : "Unticked — not recorded."}</b>{" "}
            {tickEffectNote(row)}
          </div>
        )}

        <SavedGateway row={row} fmtMin={fmtMin} />

        {/* The one case where the checkbox looks broken and isn't: the agent found
            this same change on its own, and the agent is the base `adopt` does not
            govern. Unticking here removes the ENGINE's claim to it, not the
            structure. Saying so on the row is cheaper than an analyst concluding
            the control is dead. */}
        {agentToo && (
          <div style={{ fontSize: 11.5, color: "#0e6b68", background: "#f0fdfa", border: "1px solid #99f6e4", borderRadius: 6, padding: "4px 8px", marginTop: 4 }}>
            The agent proposed this same change independently, so unticking it here will not remove it
            from the saved version — the agent&apos;s own row still writes it.
          </div>
        )}

        {row.selection_key && (
          <div style={{ fontSize: 10, color: "#c0c4cc", marginTop: 4, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}>
            {row.selection_key}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The checklist proper.
 *
 * Load renders whatever the engines applied on their own; each tick re-asks the
 * endpoint with the full `adopt` list and re-renders from the answer, so the
 * statuses on screen are always the ones that describe the payload about to be
 * POSTed rather than a local guess at what the change will do.
 */
export default function SaveHeuristicsChecklist({ selection, fmtMin }) {
  const {
    provenance,
    heuristicRows,
    declinedRows,
    warnings,
    checkedKeys,
    toggleHeuristic,
    resetHeuristicSelection,
    selectionDirty,
    applying,
    adoptError,
  } = selection;

  const [showDeclined, setShowDeclined] = React.useState(false);

  if (!provenance || !heuristicRows.length) return null;

  // Engine failure and non-reuse are already on screen above this list, via
  // ProvenanceNotices. Only the warning that invalidates THIS list belongs here.
  const listWarnings = warnings.filter((w) => !/^(failed|rerun)_/.test(w.key));
  const groups = groupHeuristicsBySource(heuristicRows);
  const savedCount = heuristicRows.filter((r) => r.status === "saved_as_structure").length;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ ...labelStyle, marginBottom: 0 }}>Heuristics in this save</div>
        <span style={{ fontSize: 11.5, color: "#6b7280" }}>
          {savedCount} of {heuristicRows.length} written into the workflow; the rest are recorded against
          the saved redesign. Untick any engine row to leave it out of the save entirely.
        </span>
        {applying && <span style={{ fontSize: 11.5, color: "#6b7280" }}>Re-composing the version…</span>}
        {selectionDirty && !applying && (
          <button
            type="button"
            className="secondary-btn sm"
            style={{ marginLeft: "auto", padding: "1px 10px", fontSize: 11 }}
            onClick={resetHeuristicSelection}
          >
            Reset to what the engines applied
          </button>
        )}
      </div>

      {adoptError && (
        <Banner tone="error" title="That selection was refused">
          {adoptError} The version below is still your previous selection, and saving is blocked until
          the selection is changed.
        </Banner>
      )}
      {listWarnings.map((w) => (
        <Banner key={w.key} tone={w.tone} title={w.title}>
          {w.detail}
          {w.keys?.length > 0 && (
            <div style={{ marginTop: 3, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10.5 }}>
              {w.keys.join(", ")}
            </div>
          )}
        </Banner>
      ))}

      {/* An empty selection is a real instruction, not an empty state, so it is
          confirmed back rather than left to be inferred from unticked boxes. */}
      {selectionDirty && checkedKeys.size === 0 && (
        <div style={{ fontSize: 12.5, color: "#075985", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8, padding: "8px 12px", marginBottom: 8 }}>
          Nothing is ticked, so no engine structure is being written. The version below is the
          agent&apos;s redesign alone — which is a valid save, not an empty one.
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, opacity: applying ? 0.55 : 1 }}>
        {groups.map((g) => (
          <div key={g.source}>
            <div style={{ fontSize: 11, fontWeight: 700, color: SOURCE_INK[g.source] || "#6b7280", marginBottom: 4 }}>
              {g.label}
              {g.source === "agent" && (
                <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                  {" "}
                  — the base of every save. Limited here to what a process version can carry; see the
                  Redesign tab for the agent&apos;s full list.
                </span>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {g.rows.map((row, i) => (
                <HeuristicRow
                  key={row.selection_key || `${row.source}:${row.code}:${i}`}
                  prov={provenance}
                  row={row}
                  checked={!!row.selection_key && checkedKeys.has(row.selection_key)}
                  onToggle={toggleHeuristic}
                  disabled={applying}
                  fmtMin={fmtMin}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Declined rows leave `applied_heuristics` entirely, so without this they
          would disappear the moment they were unticked and the analyst would have
          no way back to them. */}
      {declinedRows.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            className="secondary-btn sm"
            style={{ padding: "2px 10px", fontSize: 11 }}
            onClick={() => setShowDeclined((v) => !v)}
          >
            {showDeclined ? "Hide" : "Show"} {declinedRows.length} declined heuristic
            {declinedRows.length === 1 ? "" : "s"}
          </button>
          {showDeclined && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}>
              {declinedRows.map((row, i) => (
                <HeuristicRow
                  key={row.selection_key || `declined:${row.code}:${i}`}
                  prov={provenance}
                  row={row}
                  checked={!!row.selection_key && checkedKeys.has(row.selection_key)}
                  onToggle={toggleHeuristic}
                  disabled={applying}
                  fmtMin={fmtMin}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
